import type { ImportRunItem } from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, FileArchive } from 'lucide-react';
import { useRef, useState } from 'react';
import { Badge, Button, ErrorBox, PageHeader, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import exportScript from '@/lib/bitrix-export.js?raw';
import { formatMoment, formatSize } from '@/lib/format';

const runsQuery = {
  queryKey: ['migration', 'runs'],
  queryFn: () => api<ImportRunItem[]>('/migration/bitrix/runs'),
};

/**
 * Перенос из Битрикс24: администратор выгружает данные скриптом в браузере
 * (в Битриксе ничего не меняется) и загружает архив сюда. Повторная загрузка
 * обновляет уже перенесённое — так можно перенести всё заранее и «догнать» в день переключения.
 */
export function AdminMigrationPage() {
  const runs = useQuery({
    ...runsQuery,
    refetchInterval: (q) => (q.state.data?.some((r) => r.status === 'running') ? 3000 : false),
  });
  const running = runs.data?.some((r) => r.status === 'running') ?? false;

  return (
    <div className="max-w-[1080px]">
      <PageHeader
        title="Перенос из Битрикс24"
        description="Сотрудники, клиенты и сделки, задачи, отпуска и заявки, оборудование, охрана труда и обеды. Загружать можно сколько угодно раз: уже перенесённое обновится, дублей не будет."
      />
      <div className="flex flex-col gap-6">
        <ExportStep />
        <UploadStep disabled={running} />
        <section className="panel" aria-labelledby="mig-history">
          <header className="border-b border-line px-5 py-3.5">
            <h2 id="mig-history" className="text-[16px] font-semibold">
              История переноса
            </h2>
          </header>
          <div className="px-5 py-4">
            {runs.isPending ? (
              <Spinner />
            ) : runs.error ? (
              <ErrorBox error={runs.error} />
            ) : runs.data.length === 0 ? (
              <p className="text-steel">Перенос ещё не запускался.</p>
            ) : (
              <div className="flex flex-col gap-6">
                {runs.data.map((r, i) => (
                  <RunCard key={r.id} run={r} expanded={i === 0} />
                ))}
              </div>
            )}
          </div>
        </section>
        <CutoverChecklist />
      </div>
    </div>
  );
}

function StepHeader({ n, id, title }: { n: number; id: string; title: string }) {
  return (
    <header className="flex items-center gap-2.5 border-b border-line px-5 py-3.5">
      <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-graphite text-[12px] font-semibold text-white">
        {n}
      </span>
      <h2 id={id} className="text-[16px] font-semibold">
        {title}
      </h2>
    </header>
  );
}

function ExportStep() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(exportScript);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2500);
  };
  return (
    <section className="panel" aria-labelledby="mig-export">
      <StepHeader n={1} id="mig-export" title="Выгрузить данные из Битрикс24" />
      <div className="flex flex-col gap-3 px-5 py-4 text-[14px]">
        <ol className="flex list-decimal flex-col gap-1.5 pl-5">
          <li>
            Откройте Битрикс24 в Chrome под учёткой администратора (любую страницу, например «Сотрудники»).
          </li>
          <li>
            Откройте консоль: на Mac — <kbd className="kbd">⌥ Option + ⌘ Cmd + J</kbd>, на Windows —{' '}
            <kbd className="kbd">Ctrl + Shift + J</kbd>.
          </li>
          <li>
            Нажмите «Скопировать скрипт» ниже, вставьте в консоль и нажмите Enter. Если Chrome попросит
            разрешить вставку — напечатайте <code>allow pasting</code> (или «разрешить вставку») и вставьте
            ещё раз.
          </li>
          <li>
            Дождитесь строки «готово» (15–30 минут) — Chrome сохранит файл <code>bitrix-export-…zip</code> в
            «Загрузки». Вкладку в это время не закрывайте.
          </li>
        </ol>
        <p className="text-steel">Скрипт только читает данные — в Битриксе ничего не меняется.</p>
        <div>
          <Button variant="secondary" onClick={copy}>
            {copied ? <Check className="size-4" aria-hidden /> : <Copy className="size-4" aria-hidden />}
            {copied ? 'Скопировано' : 'Скопировать скрипт'}
          </Button>
        </div>
      </div>
    </section>
  );
}

function UploadStep({ disabled }: { disabled: boolean }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const start = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Выберите архив выгрузки');
      const form = new FormData();
      form.append('file', file);
      return api<{ id: string }>('/migration/bitrix', { method: 'POST', body: form });
    },
    onSuccess: async () => {
      setFile(null);
      if (input.current) input.current.value = '';
      await qc.invalidateQueries({ queryKey: ['migration'] });
    },
  });
  return (
    <section className="panel" aria-labelledby="mig-upload">
      <StepHeader n={2} id="mig-upload" title="Загрузить архив" />
      <div className="flex flex-col gap-3 px-5 py-4 text-[14px]">
        <label className="flex w-full max-w-[520px] cursor-pointer items-center gap-3 rounded-[var(--radius-control)] border border-dashed border-line-strong bg-sunken/50 px-4 py-3 hover:bg-sunken">
          <FileArchive className="size-5 shrink-0 text-steel" aria-hidden />
          <span className="min-w-0 flex-1 truncate">
            {file ? `${file.name} · ${formatSize(file.size)}` : 'Выберите файл bitrix-export-….zip'}
          </span>
          <input
            ref={input}
            type="file"
            accept=".zip,application/zip"
            className="sr-only"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <Button disabled={!file || disabled} loading={start.isPending} onClick={() => start.mutate()}>
            Перенести данные
          </Button>
          {disabled && <span className="text-steel">Идёт перенос — дождитесь окончания.</span>}
          {start.error && <span className="text-danger">{start.error.message}</span>}
        </div>
      </div>
    </section>
  );
}

const STATUS: Record<ImportRunItem['status'], { label: string; tone: 'neutral' | 'ok' | 'danger' }> = {
  running: { label: 'Идёт перенос…', tone: 'neutral' },
  done: { label: 'Завершён', tone: 'ok' },
  failed: { label: 'Ошибка', tone: 'danger' },
};

function RunCard({ run, expanded }: { run: ImportRunItem; expanded: boolean }) {
  const st = STATUS[run.status];
  return (
    <details open={expanded} className="group">
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-medium">{formatMoment(run.startedAt)}</span>
        <Badge tone={st.tone}>{st.label}</Badge>
        <span className="text-[13px] text-steel">
          {run.exportedAt ? `выгрузка от ${formatMoment(run.exportedAt)}` : run.fileName}
          {run.startedBy ? ` · ${run.startedBy}` : ''}
        </span>
      </summary>
      <div className="mt-3 flex flex-col gap-3">
        {run.error && <ErrorBox error={new Error(run.error)} />}
        {run.report.length > 0 && (
          <div className="relative overflow-x-auto">
            <table className="w-full min-w-[420px] text-[13.5px]">
              <thead>
                <tr className="text-left text-[12.5px] text-steel">
                  <th className="py-1.5 pr-4 font-medium">Раздел</th>
                  <th className="py-1.5 pr-4 text-right font-medium">Добавлено</th>
                  <th className="py-1.5 pr-4 text-right font-medium">Обновлено</th>
                  <th className="py-1.5 text-right font-medium">Пропущено</th>
                </tr>
              </thead>
              <tbody>
                {run.report.map((c) => (
                  <tr key={c.section} className="border-t border-line">
                    <td className="py-1.5 pr-4">{c.section}</td>
                    <td className="py-1.5 pr-4 text-right tabular-nums">{c.created || '—'}</td>
                    <td className="py-1.5 pr-4 text-right tabular-nums">{c.updated || '—'}</td>
                    <td className="py-1.5 text-right tabular-nums text-steel">{c.skipped || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {run.warnings.length > 0 && (
          <div>
            <p className="field-label">Обратите внимание</p>
            <ul className="flex list-disc flex-col gap-1 pl-5 text-[13.5px]">
              {run.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </details>
  );
}

function CutoverChecklist() {
  const items = [
    'Перенести данные заранее (шаги 1–2) и проверить: сотрудники, сделки, задачи, оборудование.',
    'В «Учётки и роли» задать пароли перенесённым сотрудникам и выдать роли («Менеджер CRM» уже выдана тем, кто вёл сделки).',
    'В «Маршруты» назначить, кто утверждает и оформляет — тогда незавершённые заявления из Битрикса уйдут на согласование.',
    'В день переключения: попросить всех закончить работу в Битриксе, сделать свежую выгрузку и загрузить её ещё раз.',
    'Сообщить сотрудникам адрес новой системы и их логины. Битрикс оставить открытым на чтение как архив.',
  ];
  return (
    <section className="panel" aria-labelledby="mig-cutover">
      <header className="border-b border-line px-5 py-3.5">
        <h2 id="mig-cutover" className="text-[16px] font-semibold">
          Переключение — порядок действий
        </h2>
      </header>
      <ol className="flex list-decimal flex-col gap-1.5 py-4 pr-5 pl-10 text-[14px]">
        {items.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ol>
    </section>
  );
}
